import { Injectable, signal } from '@angular/core';

/**
 * Service to manage the expansion state of the view
 * Provides centralized state management for table/view expansion across components
 *
 * @description
 * This service manages whether the table view is in expanded mode or not.
 * In expanded mode, the sidebar should be hidden and content should occupy full width.
 */
@Injectable({
  providedIn: 'root'
})
export class ViewExpansionStateService {
  /**
   * Signal to track if the view is expanded
   * - false: Normal view (sidebar visible, content in standard width)
   * - true: Expanded view (sidebar hidden, content full width)
   */
  private readonly isExpandedSignal = signal<boolean>(false);

  /**
   * Read-only signal for components to subscribe to
   */
  readonly isExpanded = this.isExpandedSignal.asReadonly();

  /**
   * Toggle the expansion state
   */
  toggle(): void {
    this.isExpandedSignal.update((value) => !value);
  }

  /**
   * Set the expansion state explicitly
   * @param expanded - true for expanded view, false for normal view
   */
  setExpanded(expanded: boolean): void {
    this.isExpandedSignal.set(expanded);
  }

  /**
   * Get current expansion state value
   */
  get currentState(): boolean {
    return this.isExpandedSignal();
  }
}
